import React, { useContext, useMemo, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import _ from 'lodash'
import moment from 'moment'

import dataport from 'api/dataport'
import apiReports from 'api/reports'
import templateDataport from 'api/template-dataport'

import { LoadingOverlay, FlexView, Icon, Card } from 'components/common'
import FilterableTable from 'components/common/FilterableTable'

import StockTabs from 'containers/layout/StockTabs'

import { AppContext } from 'stores/AppStore'
import { UserContext } from 'stores/UserStore'

import { reports_uri } from 'utils/constants'
import { getSpotInspectionReportDataQuery } from 'utils/dataport-query'
import { getSpotInspectionDownloadQuery } from 'utils/template-dataport-query'

const loadFile = url => {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.click()
}

const formatReports = reports => reports.map(report => {
    const formattedDate = report.inspection_date ? moment(report.inspection_date).format('DD/MM/YYYY') : 'Undated'
    return {
      ilid: report.id,
      report_date: report.inspection_date,
      name: `Inspection_Report_${formattedDate}`,
    }
  })

const InspectionReportsPage = ({ stockKey }) => {
  const { t } = useTranslation()
  const { stocks, setPageSubtitle, datasets, dataportTemplates } = useContext(AppContext)
  const { idToken } = useContext(UserContext)
  const stock = useMemo(() => _.find(stocks, { key: stockKey }), [stocks, stockKey])
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (stock) {
      setPageSubtitle(t(`stock.names.${stock.key}`) + ' - ' + t('Inspection Reports'))
    }
  }, [stock, setPageSubtitle, t])

  const downloadReport = useCallback(async id => {
    setLoading(true)
    try {
      const query = getSpotInspectionReportDataQuery(id, datasets)
      const jobId = await dataport.getJobId(query)
      const downloadQuery = getSpotInspectionDownloadQuery(jobId, datasets, dataportTemplates, id)
      const { url } = await templateDataport.loadTemplate(downloadQuery, idToken)
      loadFile(url)
      toast.info(t('documents.popupInfo'))
    }
    catch (error) {
      toast.error(error.message)
    }
    finally {
      setLoading(false)
    }
  }, [datasets, dataportTemplates, idToken, t])

  const onDownloadClick = useCallback(report_date => () => {
    downloadReport(report_date)
  }, [downloadReport])

  useEffect(() => {
    setLoading(true)
    apiReports.getSpotnspectionReportList(reports_uri.GET_SPOT_INSPECTION, {}, idToken).then(data => {
      console.log(data)
      console.log(formatReports(data))
      setReports(formatReports(data))
    }).catch(error => {
      toast.error(error.message)
    }).finally(() => {
      setLoading(false)
    })
  }, [datasets, idToken])

  const columns = useMemo(() => [
    {
      accessor: 'ilid',
      Header: t('ID')
    },
    {
      accessor: 'name',
      Header: t('Name'),
      customHeaderProps: {
        style: {
          minWidth: '150px'
        }
      }
    },
    {
      Header: t('Inspection Date'),
      accessor: ({ report_date }) => report_date ? moment(report_date).format('DD/MM/YYYY') : '',
      // Cell: ({ cell: { value } }) => value ? moment(value).format('DD/MM/YYYY') : '',
      sortType: 'date',
      customHeaderProps: {
        style: {
          minWidth: '150px'
        }
      }
    },
    {
      id: 'download',
      accessor: 'ilid',
      Header: t('Download'),
      disableFilters: true,
      customHeaderProps: {
        style: {
          minWidth: '32px'
        }
      },
      Cell: ({ cell: { value } }) => <FlexView alignItems="center" justifyContent="center" width="100%">
        <Icon name="download" width="24px" height="24px" onClick={onDownloadClick(value)} />
      </FlexView>
    }
  ], [t, onDownloadClick])

  return <FlexView flex="1" position="relative" alignSelf="stretch">
    <StockTabs stock={stock} />
    <LoadingOverlay visible={loading} />
    <Card alignSelf="stretch" padding="0px" margin="16px">
      <FilterableTable columns={columns} data={reports} />
    </Card>
  </FlexView>
}

export default InspectionReportsPage